import { BoardBuilder } from '@bo-components/boardBuilder';
import type { BoardBuilderFilters } from '@library/types/filters';
import { DEFAULT_BOARD_BUILDER_FILTERS } from '@bo-constants/filters';
import type { DateRange } from '@library/types/dates';
import { GlobalFilters } from '@library/components/GlobalFilters';
import { LeagueDependentFilters } from '@library/components/LeagueDependentFilters';
import { Loading } from '@library/components/Loading';
import { NoData } from '@library/components/NoData';
import { PageTitle } from '@library/components/PageTitle';
import { UploadProjectionsCSV } from '@bo-components/boardBuilder/UploadProjectionsCSV';
import { useDashBoard } from '@bo-hooks/useDashboard';
import { useGames } from '@bo-hooks/useGames';
import { useSentrySetDashBoardTag } from '@bo-hooks/sentry/useSentrySetDashBoardTag';

export const BoardBuilderDashboard = () => {
  useSentrySetDashBoardTag({ dashboardName: 'BoardBuilder' });

  const {
    bulkLeagueIdGroups,
    changeFilterValue,
    durations,
    filters,
    fromDate,
    handleSearch,
    handleSelectDateRange,
    handleSelectLeague,
    isLeagueError,
    isLeagueLoading,
    leagueInfo,
    leaguesList,
    searchValue,
    selectedDuration,
    selectedLeagueDurationAttributes: mainLeagueDurationAttributes,
    selectedEndDate,
    selectedLeagueId,
    selectedStartDate,
    setFilters
  } = useDashBoard({
    dashboardName: 'BOARD_BUILDER'
  });

  const { id: leagueId } = leagueInfo ?? {};
  const { positions } = mainLeagueDurationAttributes;
  const boardBuilderFilters = filters as BoardBuilderFilters;

  const {
    games: selectedGames,
    teams: selectedTeams,
    positions: selectedPositions
  } = boardBuilderFilters;

  // Sending the parent/base leagueId since games are same across the leagues/sub leagues
  // this would avoid multiple api calls when the duration/sub league is changed
  const { games, isLeagueGamesError, isLeagueGamesLoading } = useGames({
    leagueId,
    fromDate,
    toDate: fromDate
  });

  const handleGamesChange = (value: Array<string>) => {
    const newFilterState: BoardBuilderFilters = {
      ...boardBuilderFilters,
      games: value,
      teams: []
    };

    setFilters(newFilterState);
  };

  const handleTeamsChange = (value: Array<string>) => {
    changeFilterValue('teams', value);
  };

  const handlePositionsChange = (value: Array<string>) => {
    changeFilterValue('positions', value);
  };

  const handleReset = () => {
    setFilters(DEFAULT_BOARD_BUILDER_FILTERS);
    handleSearch('');
  };

  const handleChangeDate = (value: DateRange) => {
    handleSelectDateRange(value);
    handleReset();
  };

  const handleChangeLeague = (newLeagueId: string) => {
    handleSelectLeague(newLeagueId);
    handleReset();
  };

  if (isLeagueLoading) {
    return <Loading loadingMessage="Loading Leagues" />;
  }

  if (isLeagueError || !leagueInfo || !leagueId || !selectedDuration) {
    return <NoData />;
  }

  return (
    <div className="h-full w-auto">
      <section className="border-soFresh-140 w-auto border-b px-6 pb-2 pt-3">
        <PageTitle title="Board Builder" />
        <div className="pb-4">
          <div className="flex gap-4 py-4">
            <div className="grow">
              <GlobalFilters
                durations={durations}
                dateRange={false}
                handleSearch={handleSearch}
                handleSelectDateRange={handleChangeDate}
                handleSelectLeague={handleChangeLeague}
                leaguesList={leaguesList}
                searchDisabled={false}
                searchValue={searchValue}
                searchPlaceholder="Search players"
                selectedStartDate={selectedStartDate}
                selectedEndDate={selectedEndDate}
                selectedLeagueId={selectedLeagueId}
              />
            </div>
            <UploadProjectionsCSV />
          </div>
          <LeagueDependentFilters
            className="text-soClean-100 col-span-11 flex h-fit w-full gap-4"
            filters={filters}
            games={games}
            handleGamesChange={handleGamesChange}
            handleTeamsChange={handleTeamsChange}
            handlePositionsChange={handlePositionsChange}
            onReset={handleReset}
            positionValues={positions}
          />
        </div>
      </section>
      <BoardBuilder
        bulkLeagueIdGroups={bulkLeagueIdGroups}
        durations={durations}
        games={games}
        isLeagueGamesError={isLeagueGamesError}
        isLeagueGamesLoading={isLeagueGamesLoading}
        leagueInfo={leagueInfo}
        onResetSearch={() => handleSearch('')}
        selectedDuration={selectedDuration}
        selectedPositions={selectedPositions}
        searchValue={searchValue}
        selectedDate={fromDate}
        selectedGames={selectedGames}
        selectedTeams={selectedTeams}
      />
    </div>
  );
};
